/**
 * Dispute Management Constants
 * Contains all constant values used in dispute management components
 */

import {
  SafetyOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Stats, StatusFilterOption, StatsCardConfig } from "./types";

// Dispute Status Labels
export const DISPUTE_STATUS_LABELS: Record<string, string> = {
  warning_needs_response: "Warning - Needs Response",
  warning_under_review: "Warning - Under Review",
  warning_closed: "Warning - Closed",
  needs_response: "Needs Response",
  under_review: "Under Review",
  charge_refunded: "Charge Refunded",
  won: "Won",
  lost: "Lost",
};

// Dispute Status Colors
export const DISPUTE_STATUS_COLORS: Record<string, string> = {
  needs_response: "red", // Urgent - needs immediate attention
  warning_needs_response: "orange", // Warning - not urgent yet
  under_review: "blue",
  warning_under_review: "purple", // Warning - under review
  won: "green",
  lost: "orange",
  charge_refunded: "orange",
  warning_closed: "gray",
  default: "gray",
};

// Status Filter Options
export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: "all", label: "All Status" },
  { value: "needs_response", label: "Needs Response" },
  { value: "warning_needs_response", label: "Warning - Needs Response" },
  { value: "under_review", label: "Under Review" },
  { value: "warning_under_review", label: "Warning - Under Review" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "charge_refunded", label: "Charge Refunded" },
  { value: "warning_closed", label: "Warning - Closed" },
];

// Urgent Statuses (statuses that require immediate attention)
export const URGENT_STATUSES: string[] = ["needs_response"];

// Statuses that allow response submission
export const SUBMITTABLE_STATUSES: string[] = ["needs_response", "warning_needs_response"];

// Default Values
export const DEFAULT_ITEMS_PER_PAGE = 10;
export const DEFAULT_PAGE = 1;
export const DEFAULT_STATUS_FILTER = "all";

// Helper Functions
export const getStatusLabel = (status?: string): string => {
  if (!status) return "Unknown";
  return DISPUTE_STATUS_LABELS[status] || status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export const getStatusColor = (status?: string): string => {
  if (!status) return DISPUTE_STATUS_COLORS.default;
  const statusLower = status.toLowerCase();
  return DISPUTE_STATUS_COLORS[statusLower] || DISPUTE_STATUS_COLORS.default;
};

export const isUrgentStatus = (status?: string): boolean => {
  if (!status) return false;
  return URGENT_STATUSES.includes(status);
};

export const canSubmitResponse = (dispute: { status?: string; submittedAt?: string }): boolean => {
  if (!dispute || !dispute.status) return false;
  return SUBMITTABLE_STATUSES.includes(dispute.status) && !dispute.submittedAt;
};

// Statistics Cards Configuration
export const STATS_CARDS_CONFIG: StatsCardConfig[] = [
  {
    title: "Total Disputes",
    icon: SafetyOutlined,
    color: "blue",
    bgGradient: "linear(to-br, blue.400, blue.600)",
    getValue: (stats: Stats) => stats.total,
  },
  {
    title: "Needs Response",
    icon: ExclamationCircleOutlined,
    color: "red",
    bgGradient: "linear(to-br, red.400, red.600)",
    getValue: (stats: Stats) => stats.needsResponse + stats.warningNeedsResponse,
  },
  {
    title: "Under Review",
    icon: FileTextOutlined,
    color: "purple",
    bgGradient: "linear(to-br, purple.400, purple.600)",
    getValue: (stats: Stats) => stats.underReview,
  },
  {
    title: "Won",
    icon: CheckCircleOutlined,
    color: "green",
    bgGradient: "linear(to-br, green.400, green.600)",
    getValue: (stats: Stats) => stats.won,
  },
  {
    title: "Lost",
    icon: CloseCircleOutlined,
    color: "orange",
    bgGradient: "linear(to-br, orange.400, orange.600)",
    getValue: (stats: Stats) => stats.lost,
  },
];

/**
 * Build stats array for StatisticsCards component
 * @param stats - Statistics object
 * @returns Array of stat card configurations
 */
export const buildStatsArray = (stats: Stats): StatsCardConfig[] => {
  return STATS_CARDS_CONFIG.map((config) => ({
    ...config,
    value: config.getValue(stats),
  }));
};

